/* eslint-disable no-console */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {
  DraftProjectStatus,
  ProjectType,
  CaptionsDraft,
  CaptionsStylePreset,
} from '@contentlab/api/project/types';
import FeatureEditor, {
  FullHeightEditorContainer,
} from '@contentlab/components/feature-editor';
import {creditsTooltipTextForFeature} from '@contentlab/components/feature-editor/utils';
import ReplaceVideoButton from '@contentlab/components/replace-video-button';
import ReplaceVideoModal from '@contentlab/components/replace-video-modal';
import SimulatedProgressLoadingScreen from '@contentlab/components/simulated-progress-loading-screen';
import VideoDisplay, {Caption} from '@contentlab/components/video-display';
import {
  DEFAULT_DEBOUNCE_DELAY,
  FEATURE_BEST_PRACTICES_LOCALE_KEY,
} from '@contentlab/config/constants';
import {getUploadLimits} from '@contentlab/config/uploadLimits';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import useManageDraftProject from '@contentlab/hooks/mutations/use-manage-draft-project/useManageDraftProject';
import useGenerateProject from '@contentlab/hooks/use-generate-project/useGenerateProject';
import useMergedAssetErrors from '@contentlab/hooks/use-merged-asset-errors';
import useOpenPricing from '@contentlab/hooks/use-open-pricing';
import useReadyAssets from '@contentlab/hooks/use-ready-assets';
import {FontManager} from '@contentlab/services/fonts';
import contentLabStore, {
  contentLabActions,
  ErrorKind,
  UploadedVideo,
  UploadingFile,
} from '@contentlab/stores/contentLabStore';
import videoPlaybackStore, {
  videoPlaybackActions,
} from '@contentlab/stores/videoPlaybackStore';
import {UploadMode} from '@contentlab/types/UploadMode';
import {Asset} from '@contentlab/types/asset';
import {StyleConfig} from '@contentlab/types/captions';
import {FeatureName} from '@contentlab/types/project';
import {raiseContentLabError} from '@contentlab/utils/contentLabErrorHandling';
import getDefaultProjectName from '@contentlab/utils/getDefaultProjectName';
import {
  hasTranscriptionBeenEdited,
  srtItemsToTranscriptionData,
} from '@contentlab/utils/srtUtils';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {calculateVideoTokensForAssets} from '@/views/content-studio/components/utils';
import useCaptionsLanguagesQuery from '@/hooks/content-studio/use-captions-languages-query';
import useCaptionsStylesQuery from '@/hooks/content-studio/use-captions-styles-query';
import useFeatureAssets from '@/hooks/content-studio/use-feture-assets';
import {useDebouncedCallback} from '@/hooks/use-debounce';
import useNavigation from '@/hooks/use-navigation';
import CaptionsContent from './components/captions-content';
import EmptyCaptionsContent from './components/empty-captions-content';
import {StyleEditor} from './components/style-editor';
import {CaptionsTab} from './types';

function ContentDisplay({
  video,
  asset,
  currentIndex,
  uploadingFiles,
  createDropzoneConfig,
}: {
  video: UploadedVideo;
  asset?: Asset;
  currentIndex: string | null;
  uploadingFiles: Record<string, UploadingFile>;
  createDropzoneConfig: (() => object) | null;
}) {
  const [showReplaceVideoModal, setShowReplaceVideoModal] = useState(false);
  const [replaceVideoModalPresentationId, setReplaceVideoModalPresentationId] =
    useState<string>(uuid());
  const {draftCaptionsProject} = contentLabStore(
    useShallow((state) => ({
      draftCaptionsProject: state.draftCaptionsProject,
    }))
  );
  const {isPlaying} = videoPlaybackStore(
    useShallow((state) => ({
      isPlaying: state.isPlaying,
    }))
  );
  const {uiInteraction} = useUiInteractionEvent();
  const {viewPresented} = useViewPresentedEvent();
  const uploadedFile = uploadingFiles[video?.fileName || ''];

  const generateVideoFileUrl = () => {
    if (uploadedFile) {
      return URL.createObjectURL(uploadedFile.file);
    }
    return '';
  };

  // Reset video playback state when component mounts or video changes
  useEffect(() => {
    videoPlaybackActions.reset();
  }, [video?.id, asset?.id]);

  // Convert SRT items to Caption format for video display
  const getCaptions = (): Caption[] => {
    if (
      !draftCaptionsProject?.currentSrt ||
      draftCaptionsProject.currentSrt.length === 0
    ) {
      return [];
    }

    const captions = draftCaptionsProject.currentSrt.map((srtItem) => ({
      id: srtItem.id,
      start: srtItem.startSeconds,
      end: srtItem.endSeconds,
      text: srtItem.currentText,
      wordTimings: srtItem.wordTimings,
    }));

    return captions;
  };

  const captionStyle: StyleConfig | undefined =
    draftCaptionsProject.selectedStyle?.captionsStyle;

  const getSubtitleTime = () => {
    if (
      !draftCaptionsProject?.currentSrt ||
      draftCaptionsProject.currentSrt.length === 0
    ) {
      return 0;
    }

    // Get the matching item (either selected or first)
    const matchingSegment = currentIndex
      ? (draftCaptionsProject.currentSrt.find(
          (segment) => segment.id === currentIndex
        ) ?? draftCaptionsProject.currentSrt[0])
      : draftCaptionsProject.currentSrt[0];

    // Use first word's start time + 10% of duration if available, otherwise use segment start time
    if (matchingSegment.wordTimings && matchingSegment.wordTimings.length > 0) {
      const firstWord = matchingSegment.wordTimings[0];
      const wordDuration = firstWord.end - firstWord.start;
      return firstWord.start + wordDuration * 0.1;
    }

    return matchingSegment.startSeconds;
  };

  const videoProps = {
    id: 'captions-video-display',
    videoName: asset?.name || video?.fileName || '',
    videoUrl: asset?.url || generateVideoFileUrl(),
    subtitleTime: getSubtitleTime(),
    currentIndex,
    captionsData: {
      captions: getCaptions(),
      languageCode: draftCaptionsProject?.apiOriginalLanguage?.code || 'en',
    },
    captionStyle,
    onTimeUpdate: videoPlaybackActions.setCurrentTime,
    isPlayingState: {
      isPlaying,
      setIsPlaying: videoPlaybackActions.setIsPlaying,
    },
  };

  const onReplaceClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    const interactionId = uuid();
    uiInteraction({
      item_name: 'replace_video',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS,
      flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
      interaction_id: interactionId,
    });
    viewPresented({
      interaction_id: interactionId,
      view_name: CONTENT_LAB_VIEW_NAMES.REPLACE_VIDEO_MODAL,
      presentation_id: replaceVideoModalPresentationId,
      view_type: 'popup',
      view_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS,
      source: 'item_pressed',
    });
    setShowReplaceVideoModal(true);
  };

  const onPlayToggle = (playing: boolean) => {
    uiInteraction({
      item_name: 'captions_video_play',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_VIDEO_DISPLAY,
      interaction_details: playing ? 'pause' : 'play',
      flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
    });
  };
  const onTimeChange = (time: number, percentage: number) => {
    uiInteraction({
      item_name: 'captions_video_display_progress',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_VIDEO_DISPLAY,
      interaction_details: `${percentage}%`,
      flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
    });
  };

  return (
    <>
      <VideoDisplay
        {...videoProps}
        enableKeyboardControls={false}
        showPauseOverlay={false}
        onPlayToggle={onPlayToggle}
        onTimeChange={onTimeChange}
        actionComponent={<ReplaceVideoButton onClick={onReplaceClick} />}
      />
      <ReplaceVideoModal
        open={showReplaceVideoModal}
        setOpen={setShowReplaceVideoModal}
        presentationId={replaceVideoModalPresentationId}
        setPresentationId={setReplaceVideoModalPresentationId}
        createDropzoneConfig={createDropzoneConfig}
        headlineText={translate('Replacing video will reset your edits.')}
        flowName={FLOW_NAMES.CONTENT_LAB.CAPTIONS}
        resetProject={() => {
          contentLabActions.removeFeatureUploadingFiles(FeatureName.CAPTIONS);
          contentLabActions.removeFeatureUploadedVideos(FeatureName.CAPTIONS);
          contentLabActions.removeFeatureAssetErrors(FeatureName.CAPTIONS);
          contentLabActions.resetDraftCaptionsProject();
        }}
      />
    </>
  );
}

// using shared ReplaceVideoModal

function CaptionsPage() {
  const {draftCaptionsProject} = contentLabStore(
    useShallow((state) => ({
      draftCaptionsProject: state.draftCaptionsProject,
    }))
  );
  const {
    createDraftProject,
    updateDraftProject,
    resetDraftProject,
    isLoading: isManagingProjectLoading,
  } = useManageDraftProject({
    type: ProjectType.CAPTIONS,
    draftProject: draftCaptionsProject,
  });

  const {
    data: captionStyles,
    isLoading: isLoadingStyles,
    error: stylesError,
  } = useCaptionsStylesQuery();

  const {
    data: captionLanguages,
    isLoading: isLoadingLanguages,
    error: languagesError,
  } = useCaptionsLanguagesQuery();

  const {
    uploadedVideos,
    uploadingFiles,
    assetErrors: clientAssetErrors,
  } = useFeatureAssets(FeatureName.CAPTIONS);

  const {assetErrors, invalidAssetIds, validUploadedVideos} =
    useMergedAssetErrors({
      serverAssetErrors:
        draftCaptionsProject?.validationErrors?.userInput?.assets || [],
      clientAssetErrors,
      uploadedVideos,
    });

  useEffect(() => {
    if (stylesError) {
      console.error('Failed to load caption styles:', stylesError);
      raiseContentLabError({
        errorMessage:
          'Failed to load caption styles. Please try refreshing the page.',
        errorKind: ErrorKind.VALIDATION,
      });
    }
    if (languagesError) {
      console.error('Failed to load caption languages:', languagesError);
      raiseContentLabError({
        errorMessage:
          'Failed to load caption languages. Please try refreshing the page.',
        errorKind: ErrorKind.VALIDATION,
      });
    }
  }, [stylesError, languagesError]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const {uiInteraction} = useUiInteractionEvent();

  const [currentIndex, setCurrentIndex] = useState<string | null>(null);
  const [createDropzoneConfig, setCreateDropzoneConfig] = useState<
    (() => object) | null
  >(null);
  const [editingStyle, setEditingStyle] = useState<CaptionsStylePreset | null>(
    null
  );
  const [activeTab, setActiveTab] = useState<CaptionsTab>('styles');
  const {navigate} = useNavigation();
  const openPricing = useOpenPricing();

  const selectedAssetId = useMemo(() => {
    if (uploadedVideos.length === 1) {
      return uploadedVideos[0].id;
    }
    if (draftCaptionsProject) {
      return draftCaptionsProject.assetId;
    }
    return null;
  }, [uploadedVideos, draftCaptionsProject]);

  const readyAssets = useReadyAssets({
    assetIds: selectedAssetId ? [selectedAssetId] : [],
    onReady: (assets) => updateOrCreateProject(assets[0]),
    keepPreviousData: false,
  });

  const [readyAsset] = readyAssets;

  useEffect(() => {
    if (uploadedVideos.length > 0 && !draftCaptionsProject.isInitialized) {
      const firstVideo = uploadedVideos[0];
      contentLabActions.setDraftCaptionsProject({
        ...draftCaptionsProject,
        transcriptionLoaded: false,
        projectName: getDefaultProjectName('Captions'),
        assetId: firstVideo.id,
        selectedLanguages: [],
      });
    }
  }, [uploadedVideos.length, draftCaptionsProject.projectId]);

  useEffect(() => {
    const [uploadedVideo] = uploadedVideos;
    // immediately clear assets if there is an issue
    // the assetErrors are cleared only after selection of another file
    if (invalidAssetIds.has(uploadedVideo?.id)) {
      contentLabActions.removeFeatureUploadedVideos(FeatureName.CAPTIONS);
      contentLabActions.removeFeatureUploadingFiles(FeatureName.CAPTIONS);
    }
  }, [uploadedVideos, invalidAssetIds]);

  useEffect(() => {
    FontManager.initialize().catch((error) => {
      console.error('Failed to initialize FontManager:', error);
    });
  }, []);

  // Set default selected style if none is selected but styles are available
  useEffect(() => {
    if (
      captionStyles &&
      captionStyles.length > 0 &&
      !draftCaptionsProject.selectedStyle
    ) {
      const defaultStyle = captionStyles[0];
      contentLabActions.setDraftCaptionsProject({
        ...draftCaptionsProject,
        selectedStyle: defaultStyle,
      });
    }
  }, [captionStyles, draftCaptionsProject.selectedStyle, draftCaptionsProject]);

  const getProjectName = useCallback(
    () => contentLabStore.getState().draftCaptionsProject.projectName,
    []
  );

  const getCaptionsDraft = useCallback(
    (asset?: Asset): CaptionsDraft => {
      const stylePreset = draftCaptionsProject.selectedStyle;

      if (!stylePreset) {
        throw new Error(
          'No caption styles available. Please ensure styles are loaded from the backend.'
        );
      }

      const draft: CaptionsDraft = {
        assetId: asset?.id || '',
        captionsSpecs: draftCaptionsProject.selectedLanguages.map(
          (language) => ({
            outputLanguage: language,
          })
        ),
        style: stylePreset.captionsStyle,
        originalLanguage:
          draftCaptionsProject.apiOriginalLanguage?.name || 'English',
        outputTranscriptionAssetId:
          draftCaptionsProject.outputTranscriptionAssetId,
      };

      const hasEditedTranscription =
        draftCaptionsProject.currentSrt &&
        draftCaptionsProject.currentSrt.length > 0 &&
        hasTranscriptionBeenEdited(
          draftCaptionsProject.currentSrt,
          draftCaptionsProject.originalSrt
        );

      if (hasEditedTranscription) {
        const language = draftCaptionsProject.apiOriginalLanguage?.code || 'en';
        draft.editedTranscription = srtItemsToTranscriptionData(
          draftCaptionsProject.currentSrt,
          language
        );
      }

      return draft;
    },
    [
      draftCaptionsProject.selectedLanguages,
      draftCaptionsProject.originalLanguage,
      draftCaptionsProject.selectedStyle,
      draftCaptionsProject.outputTranscriptionAssetId,
      draftCaptionsProject.currentSrt,
      captionStyles,
    ]
  );

  const createProject = useCallback(
    async (asset?: Asset) =>
      createDraftProject({
        type: ProjectType.CAPTIONS,
        projectName: getProjectName(),
        draft: getCaptionsDraft(asset),
      }),
    [getCaptionsDraft, getProjectName]
  );

  const updateOrCreateProject = useCallback(
    async (asset?: Asset) => {
      if (draftCaptionsProject.projectId) {
        return updateDraftProject({
          status: DraftProjectStatus.UPDATE_DRAFT,
          projectName: getProjectName(),
          draft: getCaptionsDraft(asset),
        });
      }
      return createProject(asset);
    },
    [
      draftCaptionsProject.projectId,
      getCaptionsDraft,
      getProjectName,
      createProject,
      updateDraftProject,
    ]
  );

  const debouncedUpdateOrCreateProject = useDebouncedCallback(
    () => updateOrCreateProject(readyAsset),
    DEFAULT_DEBOUNCE_DELAY
  );

  const handleCustomize = (style: CaptionsStylePreset) => {
    const editedStyle = contentLabActions.getEditedStyle(style.id);
    const styleToEdit = editedStyle || style;

    uiInteraction({
      item_name: 'captions_style_customize',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS_STYLES,
      flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
      interaction_details: JSON.stringify({
        style_id: style.id,
        style_name: style.name,
        is_already_edited: !!editedStyle,
      }),
    });

    setEditingStyle(styleToEdit);
    setActiveTab('styles');
  };

  const handleBackToSelector = () => {
    setEditingStyle(null);
    setActiveTab('styles');
  };

  const handleSaveStyle = (style: CaptionsStylePreset) => {
    contentLabActions.saveEditedStyle(style);
    contentLabActions.setDraftCaptionsProject({
      ...draftCaptionsProject,
      selectedStyle: {
        id: style.id,
        name: style.name,
        description: style.description,
        captionsStyle: style.captionsStyle,
      },
    });
    debouncedUpdateOrCreateProject();
  };

  const handleResetStyle = (style: CaptionsStylePreset) => {
    const {originalCaptionStyles} = contentLabStore.getState();
    const originalStyle = originalCaptionStyles.find((s) => s.id === style.id);
    if (originalStyle) {
      contentLabActions.resetStyleToOriginal(style.id);

      if (draftCaptionsProject.selectedStyle?.id === style.id) {
        contentLabActions.setDraftCaptionsProject({
          ...draftCaptionsProject,
          selectedStyle: originalStyle,
        });
      }

      if (editingStyle?.id === style.id) {
        setEditingStyle(originalStyle);
      }

      debouncedUpdateOrCreateProject();
    }
  };

  const handleStyleChange = (style: CaptionsStylePreset) => {
    setEditingStyle(style);
    contentLabActions.saveEditedStyle(style);
  };

  const isLoading = useMemo(() => {
    if (uploadedVideos.length === 0) {
      return false;
    }

    return (
      !draftCaptionsProject.transcriptionLoaded ||
      isLoadingStyles ||
      isLoadingLanguages ||
      !captionStyles ||
      !captionLanguages
    );
  }, [
    uploadedVideos,
    draftCaptionsProject.transcriptionLoaded,
    isLoadingStyles,
    isLoadingLanguages,
    captionStyles,
    captionLanguages,
  ]);

  const renderUploadContent = useCallback(
    ([video]: UploadedVideo[]) => (
      <ContentDisplay
        video={video}
        asset={readyAsset}
        currentIndex={currentIndex}
        uploadingFiles={uploadingFiles}
        createDropzoneConfig={createDropzoneConfig}
      />
    ),
    [readyAsset, currentIndex, uploadingFiles, createDropzoneConfig]
  );

  const onFilePickerReady = useCallback(
    (dropzoneConfigFactory: () => object) => {
      setCreateDropzoneConfig(() => dropzoneConfigFactory);
    },
    [setCreateDropzoneConfig]
  );

  const onFilesSelected = useCallback(() => {
    // clear all current asset errors upon file selection
    contentLabActions.removeFeatureAssetErrors(FeatureName.CAPTIONS);
  }, []);

  const getRequiredCredits = () => {
    if (validUploadedVideos.length === 0) {
      return 0;
    }
    return (
      calculateVideoTokensForAssets(validUploadedVideos) *
      draftCaptionsProject.selectedLanguages.length
    );
  };

  const updateProject = async (status: DraftProjectStatus) => {
    await updateDraftProject({
      status,
      projectName: getProjectName(),
      draft: getCaptionsDraft(readyAsset),
    });
  };
  const generateProject = useGenerateProject({
    getRequiredCredits,
    setIsSubmitting,
    draftProject: draftCaptionsProject,
    defaultProjectName: 'Captions',
    updateProject,
    resetProject: resetDraftProject,
  });

  const handleGenerateClick = async () => {
    setIsSubmitting(true);
    try {
      uiInteraction({
        item_name: 'execute_captions_project',
        interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS,
        flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
        interaction_id: uuid(),
      });

      if (!draftCaptionsProject.isInitialized) {
        await createProject(readyAsset);
      }
      await generateProject();
    } catch (error) {
      // Error handled silently
    }
    setIsSubmitting(false);
  };

  const isGenerateButtonDisabled = useMemo(() => {
    return (
      uploadedVideos.length === 0 ||
      uploadedVideos.some((video) => video.isUploading) ||
      isSubmitting ||
      !draftCaptionsProject.projectName ||
      isManagingProjectLoading ||
      readyAsset?.status !== 'ready' ||
      draftCaptionsProject.selectedLanguages.length === 0
    );
  }, [
    uploadedVideos,
    isSubmitting,
    draftCaptionsProject.projectName,
    draftCaptionsProject.selectedLanguages,
    isManagingProjectLoading,
    readyAsset?.status,
  ]);

  const isGenerateButtonLoading = useMemo(
    () =>
      draftCaptionsProject.transcriptionLoaded &&
      (isSubmitting ||
        uploadedVideos.some((video) => video.isUploading) ||
        isLoading ||
        isManagingProjectLoading),
    [
      draftCaptionsProject.transcriptionLoaded,
      isSubmitting,
      uploadedVideos,
      isLoading,
      isManagingProjectLoading,
    ]
  );

  const isFileLoading = useMemo(() => {
    if (isLoading) {
      return true;
    }
    return (
      validUploadedVideos.length > 0 &&
      !draftCaptionsProject.transcriptionLoaded
    );
  }, [
    isLoading,
    validUploadedVideos,
    draftCaptionsProject.transcriptionLoaded,
  ]);

  const generateProjectDescription = useMemo(() => {
    if (isLoading) {
      return '';
    }
    if (isSubmitting) {
      return '';
    }
    const isUploading = uploadedVideos.some((video) => video.isUploading);
    if (!isUploading && uploadedVideos.length === 0) {
      return translate('Upload a video to start generating captions');
    }
    return '';
  }, [uploadedVideos, isLoading, isSubmitting]);

  const getFeatureDetailsComponent = () => {
    if (
      draftCaptionsProject.isInitialized &&
      draftCaptionsProject.transcriptionLoaded &&
      captionStyles &&
      captionLanguages &&
      !isLoadingStyles &&
      !isLoadingLanguages
    ) {
      if (editingStyle) {
        return (
          <StyleEditor
            style={editingStyle}
            onStyleChange={handleStyleChange}
            onBack={handleBackToSelector}
            onSave={handleSaveStyle}
            onReset={handleResetStyle}
          />
        );
      }
      return (
        <CaptionsContent
          draftCaptionsProject={draftCaptionsProject}
          projectId={draftCaptionsProject?.projectId || ''}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          captionStyles={captionStyles || []}
          onStyleChange={debouncedUpdateOrCreateProject}
          onTranscriptionChange={debouncedUpdateOrCreateProject}
          onCustomizeStyle={handleCustomize}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      );
    }
    if (isLoading) {
      return <SimulatedProgressLoadingScreen />;
    }
    return <EmptyCaptionsContent />;
  };

  const getRightColumnOverride = () => {
    if (editingStyle) {
      return (
        <FullHeightEditorContainer>
          <StyleEditor
            style={editingStyle}
            onStyleChange={handleStyleChange}
            onBack={handleBackToSelector}
            onSave={handleSaveStyle}
            onReset={handleResetStyle}
          />
        </FullHeightEditorContainer>
      );
    }
    return undefined;
  };

  const hideGenerateSection = useMemo(
    () =>
      !draftCaptionsProject.isInitialized ||
      validUploadedVideos.length === 0 ||
      !readyAsset,
    [draftCaptionsProject.isInitialized, validUploadedVideos, readyAsset]
  );

  return (
    <FeatureEditor
      feature={FeatureName.CAPTIONS}
      featureDetailsProps={{
        featureDetailsComponent: getFeatureDetailsComponent(),
      }}
      renameProjectProps={{
        projectName: draftCaptionsProject?.projectName || '',
        onBlur: (name) => {
          updateOrCreateProject();
          uiInteraction({
            item_name: 'rename_project',
            interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS,
            flow_name: FLOW_NAMES.CONTENT_LAB.CAPTIONS,
            interaction_details: JSON.stringify({
              project_name: name,
            }),
          });
        },
        projectNameChange: (name) => {
          contentLabActions.updateDraftCaptionsProjectName(name);
        },
        projectNamePlaceholder: translate('Name your Captions project'),
      }}
      generateProjectProps={{
        description: generateProjectDescription,
        assetErrors,
        readyAssets,
        onGenerateClick: handleGenerateClick,
        creditsCost: getRequiredCredits(),
        disabled: isGenerateButtonDisabled,
        isLoading: isGenerateButtonLoading,
        tooltipText: creditsTooltipTextForFeature(
          FeatureName.CAPTIONS,
          validUploadedVideos.length,
          draftCaptionsProject.selectedLanguages.length
        ),
      }}
      fileUploaderProps={{
        title: translate('Upload a video with speech'),
        guideTitle: translate('must_contain_clear_speech_with_limits', {
          maxDuration: translate(
            getUploadLimits(FeatureName.CAPTIONS).maxDurationDisplayKey
          ),
          maxSize: translate(
            getUploadLimits(FeatureName.CAPTIONS).maxFileSizeDisplayKey
          ),
        }),
        guideDescription: translate(
          FEATURE_BEST_PRACTICES_LOCALE_KEY[FeatureName.CAPTIONS]
        ),
        shortenGuideText: translate(
          FEATURE_BEST_PRACTICES_LOCALE_KEY[FeatureName.CAPTIONS]
        ),
        assetErrors: isLoading ? [] : assetErrors,
        renderUploadContent: draftCaptionsProject.transcriptionLoaded
          ? renderUploadContent
          : undefined,
        allowMultiUpload: false,
        onFilePickerReady,
        onFilesSelected,
        uploadMode: UploadMode.SINGLE,
        isLoading: isFileLoading,
        onLearnMoreClick: () => {
          contentLabActions.setIsLearnMoreModalOpen(true);
          uiInteraction({
            item_name: 'learn more',
            interaction_location: CONTENT_LAB_VIEW_NAMES.CAPTIONS,
          });
        },
      }}
      rightColumnOverride={getRightColumnOverride()}
      hideGenerateSection={hideGenerateSection}
    />
  );
}

export default CaptionsPage;
